import renderWinProbabilityTable from './tables/win-probability.ts'
import renderWinProbabilityByCountTable from './tables/win-probability-by-count.ts'
import renderRunExpectancyByCountSelectedTable from './tables/run-expectancy-counts.ts'
import renderRunExpectancyByRunnerSelectedTable from './tables/run-expectancy-runners.ts'

import { GameSituation } from './types/game-situation.ts'
import isSituationSet from './utils/helpers.ts'
import {
    getGameSituation,
    getGameSituationSummary,
    resetGameSituation,
} from './game-situation.ts'
import {
    flattenRunExpectancyByRunners,
    flattenRunExpectancyByCount,
} from './utils/flatten.ts'

import {
    WinProbabilityRow,
    WinProbabilitySituationRow,
} from './types/win-probabilty.ts'

import {
    RunExpectancyRunners,
    RawRunExpectancy,
    RunExpectancyByRunnersRow,
} from './types/run-expectancy.ts'

import populateFormValuesByQueryString from './utils/populate-form-values.ts'

import sendQuery from './utils/query.ts'

const lblType = document.getElementById('lblType') as HTMLElement | null
const ddlType = document.getElementById('ddlType') as HTMLSelectElement | null

const winProbButton = document.getElementById('btnWinProbability')
const winProbCountButton = document.getElementById('btnWinProbabilityCount')

const balls = document.getElementById('balls')
const strikes = document.getElementById('strikes')
const situationContainer = document.getElementById('situation-container')
const summaryContainer = document.getElementById('summary-container')

populateFormValuesByQueryString()
updateFormControls()
emitSituation(false)

// All requests sent through here
async function emitSituation(isSituation: boolean): Promise<void> {
    const gameSituation = getGameSituation()

    const type =
        document.querySelector<HTMLSelectElement>('select[name="ddlType"]')
            ?.value ?? null
    if (type === 'winexp' && isSituation) {
        sendWinProbabilitySituationRequest(gameSituation).then((data) => {
            appendSituationToSummary(data)
        })
    } else if (type === 'winexp') {
        clearSummary()

        await sendWinProbabilityRequest(gameSituation).then((data) => {
            renderWinProbabilityTable(data)
        })

        gameSituation.is_by_count = true

        await sendWinProbabilityRequest(gameSituation).then((data) => {
            renderWinProbabilityByCountTable(data)
        })
        sendSituationQuery()
    } else {
        sendRunExpectancyRequest(gameSituation, 'runexp-runners').then(
            (data) => {
                const rows = flattenRunExpectancyByRunners(data)
                renderRunExpectancyByRunnerSelectedTable(rows)
            }
        )

        sendRunExpectancyRequest(gameSituation, 'runexp-count').then((data) => {
            const rows = flattenRunExpectancyByCount(data)

            renderRunExpectancyByCountSelectedTable(rows)
        })
    }

    populateUrl()
}

function appendSituationToSummary(
    winProbabilitySituation: WinProbabilitySituationRow[]
) {
    const currentWinPctEl = document.getElementById('current-win-pct')

    let currentWinPct = 0
    if (currentWinPctEl) {
        currentWinPct = Number(currentWinPctEl.innerHTML)
    }

    const summaryEl = document.getElementById('summary-situation')

    if (!summaryEl || !winProbabilitySituation) return

    if (winProbabilitySituation.length === 0) {
        summaryEl.innerHTML = `<i>* No data is available for this particular situation. Please try another selection.</i>`
    } else {
        const htmlContent = winProbabilitySituation
            .map((winProb: WinProbabilitySituationRow) => {
                const winPct = winProb?.next_bat_wins
                    ? winProb.next_bat_wins * 100
                    : 0

                const winPctDiff = winPct - Number(currentWinPct)
                const displayPct = winPct.toFixed(1)
                let displayDiff: string
                const formattedDiff = winPctDiff.toFixed(1)

                if (winPct === 0 || winPct === 100) {
                    displayDiff =
                        '<span style="color: rgb(217, 40, 47);">GAME OVER</span>'
                } else if (winPctDiff > 0) {
                    displayDiff = `<span style="color: rgb(217, 40, 47);">+${formattedDiff}</span>`
                } else if (winPctDiff < 0) {
                    displayDiff = `<span style="color: blue;">${formattedDiff}</span>`
                } else {
                    displayDiff = formattedDiff
                }

                return `<div>${getGameSituationSummary(
                    winProb
                )} <strong>${displayPct}% WP ${
                    typeof winPctDiff === 'number' ? `(${displayDiff}) ` : ``
                }</strong></div>`
            })
            .join('')

        summaryEl.innerHTML = htmlContent
    }
}

function populateUrl() {
    const queryString = JSON.stringify(getGameSituation())
    const type = document.getElementById('ddlType') as HTMLSelectElement | null

    if (type) {
        const { value } = type

        // Encode the components to handle braces, quotes, and spaces
        const encodedQs = encodeURIComponent(queryString)
        const encodedType = encodeURIComponent(value)

        window.history.pushState(
            getGameSituation(),
            '',
            `/game-strategy-explorer?type=${encodedType}&qs=${encodedQs}`
        )
    }
}

async function sendWinProbabilityRequest(
    gameSituation: Partial<GameSituation>
): Promise<WinProbabilityRow[]> {
    const query = new URLSearchParams({
        type: 'winexp',
        params: JSON.stringify(gameSituation),
    })

    const data = await sendQuery<WinProbabilityRow[]>(query)
    return data
}

async function sendWinProbabilitySituationRequest(
    gameSituation: Partial<GameSituation>
): Promise<WinProbabilitySituationRow[]> {
    const query = new URLSearchParams({
        type: 'winexp-situation',
        params: JSON.stringify(gameSituation),
    })

    const data = await sendQuery<WinProbabilitySituationRow[]>(query)
    return data
}

function sendRunExpectancyRequest(
    situation: Partial<GameSituation>,
    type: 'count'
): Promise<RunExpectancyRunners[]>
function sendRunExpectancyRequest(
    situation: Partial<GameSituation>,
    type: string
): Promise<RawRunExpectancy[]>

async function sendRunExpectancyRequest(
    gameSituation: Partial<GameSituation>,
    type: string
): Promise<unknown> {
    const query = new URLSearchParams({
        type,
        params: JSON.stringify(gameSituation),
    })

    if (type === 'count') {
        return sendQuery<RunExpectancyByRunnersRow[]>(query)
    }
    return sendQuery<RawRunExpectancy[]>(query)
}

document.addEventListener('change', async (e: Event) => {
    const target = e.target as HTMLElement | null
    if (!target) return

    if (target.id === 'ddlType') {
        clearSummary()
        updateFormControls()
        emitSituation(false)
    } else {
        await emitSituation(false)
        sendSituationQuery()
    }
})

async function sendSituationQuery(): Promise<void> {
    const showSummarySituation = isSituationSet()

    if (showSummarySituation) {
        emitSituation(true)
    } else {
        // clearSummary()
    }
}

function clearSummary() {
    const summary = document.getElementById('summary')
    const summarySituation = document.getElementById('summary-situation')

    if (summary && summarySituation) {
        summary.innerHTML = ''
        summarySituation.innerHTML = ''
    }
}

function updateFormControls() {
    // hide all controls and show them based on selections
    toggleByClass('toggle-control', false)

    const selectedOption = ddlType?.options[ddlType.selectedIndex]
    const type = selectedOption?.value ?? null

    if (lblType) {
        lblType.textContent = selectedOption?.text ?? ''
    }

    const runExpMessage = document.getElementById('run-exp-message')
    const winExpMessage = document.getElementById('win-exp-message')

    if (type === 'runexp') {
        toggleByClass('run-exp', true)
        toggleByClass('win-exp', false)
        strikes?.classList.remove('hidden')
        balls?.classList.remove('hidden')
        situationContainer?.classList.add('hidden')
        summaryContainer?.classList.add('hidden')

        if (runExpMessage) runExpMessage.style.display = 'block'
        if (winExpMessage) winExpMessage.style.display = 'none'
    }

    if (type === 'winexp') {
        toggleByClass('win-exp', true)
        toggleByClass('run-exp', false)
        if (winExpMessage) winExpMessage.style.display = 'block'
        if (runExpMessage) runExpMessage.style.display = 'none'

        if (winProbButton?.classList.contains('btn-active')) {
            strikes?.classList.add('hidden')
            balls?.classList.add('hidden')
            situationContainer?.classList.remove('hidden')
            summaryContainer?.classList.remove('hidden')
        } else {
            strikes?.classList.remove('hidden')
            balls?.classList.remove('hidden')
            situationContainer?.classList.add('hidden')
            summaryContainer?.classList.add('hidden')
        }
    }
}

function toggleByClass(className: string, show: boolean) {
    document
        .querySelectorAll<HTMLElement>(`.${className}`)
        .forEach((el: HTMLElement) => {
            if (el) {
                const element = el
                element.style.display = show ? '' : 'none'
            }
        })
}

document.querySelectorAll<HTMLElement>('.base').forEach((base) => {
    const baseEl = base

    // initialize state if missing
    if (!base.dataset.selected) {
        baseEl.dataset.selected = 'false'
        base.setAttribute('aria-pressed', 'false')
    }

    base.addEventListener('click', () => {
        const isSelected = base.dataset.selected === 'true'

        if (isSelected) {
            // turn OFF
            baseEl.dataset.selected = 'false'
            baseEl.setAttribute('aria-pressed', 'false')
            baseEl.classList.remove('base-selected')
            baseEl.classList.add('base-hatched')
        } else {
            // turn ON
            baseEl.dataset.selected = 'true'
            baseEl.setAttribute('aria-pressed', 'true')
            baseEl.classList.add('base-selected')
            baseEl.classList.remove('base-hatched')
        }
        emitSituation(false)
    })
})

const clearButton = document.getElementById('btnClear')

if (clearButton) {
    clearButton.addEventListener('click', (event: MouseEvent): void => {
        // Prevent default form submission behavior if btnSubmit is a submit type
        event.preventDefault()

        resetGameSituation()
        emitSituation(false)
    })
}

const countTableWrapper = document.getElementById(
    'game-strategy-explorer-by-count-table-wrapper'
)
const mainTableWrapper = document.getElementById(
    'game-strategy-explorer-table-wrapper'
)

if (winProbButton) {
    winProbButton.addEventListener('click', (event: MouseEvent): void => {
        // Prevent default form submission behavior if btnSubmit is a submit type
        event.preventDefault()
        toggleWinProbForm()
    })
}

if (winProbCountButton) {
    winProbCountButton.addEventListener('click', (event: MouseEvent): void => {
        // Prevent default form submission behavior if btnSubmit is a submit type
        event.preventDefault()
        toggleWinProbCountForm()
    })
}

function toggleWinProbForm(): void {
    countTableWrapper?.classList.add('hidden')
    mainTableWrapper?.classList.remove('hidden')
    balls?.classList.add('hidden')
    strikes?.classList.add('hidden')
    summaryContainer?.classList.remove('hidden')
    situationContainer?.classList.remove('hidden')
    winProbButton?.classList.add('btn-active')
    winProbCountButton?.classList.remove('btn-active')
}

function toggleWinProbCountForm(): void {
    countTableWrapper?.classList.remove('hidden')
    mainTableWrapper?.classList.add('hidden')
    balls?.classList.remove('hidden')
    strikes?.classList.remove('hidden')
    summaryContainer?.classList.add('hidden')
    situationContainer?.classList.add('hidden')
    winProbButton?.classList.remove('btn-active')
    winProbCountButton?.classList.add('btn-active')
}
